/**
 * preDepartureChecksLibrary.js
 *
 * Single source of truth for pre-departure checks.
 *
 * IMPORTANT: WBC3 does NOT mandate a specific pre-departure checklist
 * for crewed workboats. The only explicit checklist in WBC3 Appendix 8
 * is for Remotely Operated Unmanned Vessels (ROUVs).
 *
 * This library provides a MINIMAL starting point based on IMPLIED
 * requirements from various WBC3 sections. Companies should customize
 * this checklist to suit their vessel type and operations.
 *
 * ALL ITEMS ARE EDITABLE - companies can modify, remove, or add items
 * to create a checklist that works for their specific needs.
 */

export const CONFIG_VERSION = 4;

// ============================================================================
// DEFAULT PRE-DEPARTURE CHECKS - Minimal starting point
// Based on implied WBC3 requirements - ALL EDITABLE
// ============================================================================
export const DEFAULT_CHECKS = [
  {
    id: 'weather',
    text: 'Weather forecast assessed suitable for voyage',
    category: 'planning',
    regulation: 'WBC3 7.3.2 (implied)',
    required: true,
    locked: false  // EDITABLE - companies can customize
  },
  {
    id: 'nav_equipment',
    text: 'Navigation equipment operational',
    category: 'navigation',
    regulation: 'WBC3 18 (general)',
    required: true,
    locked: false
  },
  {
    id: 'steering_test',
    text: 'Steering tested',
    category: 'machinery',
    regulation: 'WBC3 12.8.3 (implied)',
    required: true,
    locked: false
  },
  {
    id: 'comms_check',
    text: 'Communications checked',
    category: 'communications',
    regulation: 'WBC3 17.2 (implied)',
    required: true,
    locked: false
  },
  {
    id: 'crew_brief',
    text: 'Crew briefed on voyage plan',
    category: 'crew',
    regulation: 'WBC3 7.3.1 (implied)',
    required: true,
    locked: false
  },
  {
    id: 'lsa_ready',
    text: 'Life-saving equipment ready for use',
    category: 'safety',
    regulation: 'WBC3 14 (general)',
    required: true,
    locked: false
  }
];

// ============================================================================
// LEGACY REQUIRED_CHECKS - For backward compatibility
// Now points to DEFAULT_CHECKS with locked: false
// ============================================================================
export const REQUIRED_CHECKS = DEFAULT_CHECKS;

// ============================================================================
// STANDARD CHECKS LIBRARY - Additional checks companies may want to add
// ============================================================================
export const STANDARD_CHECKS_LIBRARY = {
  navigation: {
    label: 'Navigation Equipment',
    description: 'Navigation aids and bridge equipment',
    checks: [
      { id: 'nav_lights', text: 'Navigation lights tested and operational', regulation: 'COLREGs' },
      { id: 'compass', text: 'Compass checked and error noted', regulation: 'WBC3 18.5' },
      { id: 'gps', text: 'GPS operational and position verified', regulation: 'WBC3 18.5' },
      { id: 'radar', text: 'Radar operational (if fitted)', regulation: 'WBC3 18.5' },
      { id: 'ais', text: 'AIS transmitting correct data (if fitted)', regulation: 'WBC3 18.4' },
      { id: 'echo_sounder', text: 'Echo sounder operational (if fitted)', regulation: 'WBC3 18.5' },
      { id: 'charts', text: 'Charts/electronic charts available for voyage area', regulation: 'WBC3 18.5' }
    ]
  },
  communications: {
    label: 'Communications',
    description: 'Radio and signalling equipment',
    checks: [
      { id: 'vhf_test', text: 'VHF radio tested - transmit and receive', regulation: 'WBC3 17.2' },
      { id: 'dsc', text: 'DSC operational and MMSI correct (if fitted)', regulation: 'WBC3 17.2' },
      { id: 'mobile', text: 'Mobile phone charged (backup comms)', regulation: 'Best Practice' },
      { id: 'epirb', text: 'EPIRB armed and in bracket (if fitted)', regulation: 'WBC3 17.3' }
    ]
  },
  safety: {
    label: 'Safety Equipment',
    description: 'Life-saving and safety gear',
    checks: [
      { id: 'lifejackets', text: 'Lifejackets available for all persons on board', regulation: 'WBC3 14.4' },
      { id: 'lifebuoys', text: 'Lifebuoys secured and ready for use', regulation: 'WBC3 14.5' },
      { id: 'flares', text: 'Distress flares in date and accessible', regulation: 'WBC3 14.6' },
      { id: 'first_aid', text: 'First aid kit complete and accessible', regulation: 'WBC3 15.1' },
      { id: 'fire_extinguishers', text: 'Fire extinguishers in place and in date', regulation: 'WBC3 16.2' },
      { id: 'liferaft', text: 'Liferaft secured and service in date (if fitted)', regulation: 'WBC3 14.2' },
      { id: 'mob_equipment', text: 'MOB recovery equipment ready', regulation: 'WBC3 14.7' }
    ]
  },
  vessel: {
    label: 'Vessel Condition',
    description: 'Hull and deck checks',
    checks: [
      { id: 'watertight', text: 'Watertight doors and hatches secured', regulation: 'WBC3 9.4' },
      { id: 'freeing_ports', text: 'Freeing ports clear and operational', regulation: 'WBC3 9.5' },
      { id: 'bilges', text: 'Bilges checked and pumps tested', regulation: 'WBC3 13.3' },
      { id: 'mooring_lines', text: 'Mooring lines clear for departure', regulation: 'Best Practice' },
      { id: 'deck_clear', text: 'Deck clear and gear secured', regulation: 'Best Practice' },
      { id: 'anchors', text: 'Anchor ready for immediate use', regulation: 'WBC3 9.8' }
    ]
  },
  machinery: {
    label: 'Machinery',
    description: 'Engine and mechanical checks',
    checks: [
      { id: 'engine_checks', text: 'Main engine checks completed', regulation: 'WBC3 12.8' },
      { id: 'oil_levels', text: 'Engine oil levels checked', regulation: 'Best Practice' },
      { id: 'coolant', text: 'Coolant levels checked', regulation: 'Best Practice' },
      { id: 'battery', text: 'Battery condition satisfactory', regulation: 'WBC3 12.6' },
      { id: 'fuel_check', text: 'Fuel level sufficient for voyage plus reserve', regulation: 'WBC3 12.4' }
    ]
  }
};

// ============================================================================
// CONDITIONAL CHECKS - Added based on voyage type
// ============================================================================
export const CONDITIONAL_CHECKS = {
  passengers: [
    {
      id: 'passenger_brief',
      text: 'Passenger/personnel safety briefing completed',
      category: 'passengers',
      regulation: 'WBC3 8.9.1 (required for IP)',
      required: true,
      condition: 'hasPassengers'
    },
    {
      id: 'passenger_lifejackets',
      text: 'Passenger lifejackets issued/demonstrated',
      category: 'passengers',
      regulation: 'WBC3 14.4',
      required: true,
      condition: 'hasPassengers'
    }
  ],
  night: [
    {
      id: 'nav_lights_night',
      text: 'All navigation lights checked - correct configuration',
      category: 'navigation',
      regulation: 'COLREGs Part C',
      required: true,
      condition: 'isNightVoyage'
    }
  ],
  towing: [
    {
      id: 'tow_gear',
      text: 'Towing gear inspected and ready',
      category: 'operations',
      regulation: 'WBC3 26.2',
      required: true,
      condition: 'isTowingOperation'
    },
    {
      id: 'tow_brief',
      text: 'Towing operation briefed with crew',
      category: 'operations',
      regulation: 'WBC3 26.3',
      required: true,
      condition: 'isTowingOperation'
    }
  ],
  offshore: [
    {
      id: 'transfer_equipment',
      text: 'Personnel transfer equipment inspected',
      category: 'operations',
      regulation: 'WBC3 25.5',
      required: true,
      condition: 'isOffshoreOperation'
    }
  ]
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all standard checks as flat array with category info
 */
export const getAllStandardChecks = () => {
  const checks = [];
  Object.entries(STANDARD_CHECKS_LIBRARY).forEach(([categoryId, category]) => {
    category.checks.forEach(check => {
      checks.push({
        ...check,
        category: categoryId,
        categoryLabel: category.label,
        required: false,
        locked: false
      });
    });
  });
  return checks;
};

/**
 * Categories for UI organisation
 */
export const CHECK_CATEGORIES = Object.entries(STANDARD_CHECKS_LIBRARY).map(
  ([id, cat]) => ({
    id,
    label: cat.label,
    description: cat.description,
    checkCount: cat.checks.length
  })
);

/**
 * Default optional checks to show on voyage start
 * (Empty by default - minimal checklist philosophy)
 */
export const getDefaultEnabledChecks = () => {
  return [];
};

/**
 * Get the full aligned check structure for StartVoyageFullScreen
 * Backward compatible: accepts boolean or options object
 *
 * @param {boolean|object} optionsOrHasPassengers - boolean for backward compat, or options object
 */
export const getVoyageStartChecks = (optionsOrHasPassengers = false) => {
  // Backward compatibility: if boolean, convert to options
  let options = optionsOrHasPassengers;
  if (typeof optionsOrHasPassengers === 'boolean') {
    options = { hasPassengers: optionsOrHasPassengers };
  }

  const {
    hasPassengers = false,
    isNightVoyage = false,
    isTowing = false,
    isOffshore = false
  } = options;

  const checks = {
    required: DEFAULT_CHECKS.map(c => ({
      id: c.id,
      text: c.text,
      required: true,
      type: c.type,
      regulation: c.regulation
    })),
    optional: getDefaultEnabledChecks().map(c => ({
      id: c.id,
      text: c.text,
      required: false,
      regulation: c.regulation
    })),
    conditional: {}
  };

  if (hasPassengers) {
    checks.conditional.passengers = CONDITIONAL_CHECKS.passengers.map(c => ({
      id: c.id,
      text: c.text,
      required: c.required
    }));
  }
  if (isNightVoyage) {
    checks.conditional.night = CONDITIONAL_CHECKS.night.map(c => ({
      id: c.id,
      text: c.text,
      required: c.required
    }));
  }
  if (isTowing) {
    checks.conditional.towing = CONDITIONAL_CHECKS.towing.map(c => ({
      id: c.id,
      text: c.text,
      required: c.required
    }));
  }
  if (isOffshore) {
    checks.conditional.offshore = CONDITIONAL_CHECKS.offshore.map(c => ({
      id: c.id,
      text: c.text,
      required: c.required
    }));
  }

  return checks;
};

/**
 * Get total check count for a given set of options
 */
export const getCheckCount = (options = {}) => {
  const checks = getVoyageStartChecks(options);
  let count = checks.required.length + checks.optional.length;
  Object.values(checks.conditional).forEach(group => {
    count += group.length;
  });
  return count;
};
